// ════════════════════════════════════════════════════════════════════════════
// Fenced blocks, WITH their character spans.
//
// `extract-map-fences.ts` answers "which fences are map blocks" and hands back
// bodies. This module answers the same question for the two attach-result
// fences and hands back OFFSETS as well, because both consumers here edit the
// document rather than read from it:
//
//   the materialiser  replaces an authored ``` attach-result ``` block with a
//                     rendered sentinel, in place, leaving every other byte
//                     of the body untouched.
//   the patch guard   decides whether an anchored edit's span overlaps a
//                     sentinel's interior — a question only offsets answer.
//
// Nesting is CommonMark's: the first opener wins and everything up to a
// matching closer is body, so a fence spelled inside another fence is text.
// Both markers are read (backtick and tilde) and a closer must repeat the
// opener's marker at no less than its length.
// ════════════════════════════════════════════════════════════════════════════

import { ANY_FENCE_OPEN_REGEX } from './attached-result-constants.js';

/** One fenced block, located. Offsets index the ORIGINAL string, so
 *  `markdown.slice(span.start, span.end)` is the block including its fence
 *  lines and `slice(bodyStart, bodyEnd)` is the body alone. */
export interface FenceSpan {
  /** Info string, `trim().toLowerCase()`d — the render side's normalization. */
  readonly info: string;
  readonly start: number;
  readonly end: number;
  readonly bodyStart: number;
  readonly bodyEnd: number;
  readonly body: string;
  /** False when the input ended before a closer. CommonMark closes such a
   *  fence at end of input, and a model that stopped mid-block still wrote
   *  real links — so the span is reported rather than discarded. */
  readonly closed: boolean;
}

/** A line of the input with its offset, so the scan never re-derives one. */
interface LocatedLine {
  readonly text: string;
  readonly start: number;
  /** One past the line's last character, INCLUDING its newline where it had
   *  one — a block's end is the start of whatever follows it. */
  readonly end: number;
}

/** Opener being accumulated. Never escapes this module. */
interface OpenFence {
  readonly marker: string;
  readonly length: number;
  readonly info: string;
  readonly start: number;
  readonly bodyStart: number;
  bodyEnd: number;
}

function locateLines(markdown: string): LocatedLine[] {
  const lines: LocatedLine[] = [];
  let cursor = 0;
  markdown.split('\n').forEach((rawLine, index, all) => {
    const isLast = index === all.length - 1;
    const end = cursor + rawLine.length + (isLast ? 0 : 1);
    lines.push({ text: stripCarriageReturn(rawLine), start: cursor, end });
    cursor = end;
  });
  return lines;
}

function stripCarriageReturn(line: string): string {
  return line.endsWith('\r') ? line.slice(0, -1) : line;
}

function readOpener(input: { line: LocatedLine }): OpenFence | null {
  const match = input.line.text.match(ANY_FENCE_OPEN_REGEX);
  if (match === null) return null;
  const run = match[1] ?? '';
  return {
    marker: run[0] ?? '',
    length: run.length,
    info: (match[2] ?? '').trim().toLowerCase(),
    start: input.line.start,
    bodyStart: input.line.end,
    bodyEnd: input.line.end,
  };
}

/** A closer repeats the opener's marker at no less than its length and carries
 *  nothing else. `readOpener` would also match such a line, so the open-fence
 *  branch must be consulted first — which is why this reads the same regex
 *  rather than a second one. */
function closes(input: { open: OpenFence; line: LocatedLine }): boolean {
  const match = input.line.text.match(ANY_FENCE_OPEN_REGEX);
  if (match === null) return false;
  const run = match[1] ?? '';
  const info = (match[2] ?? '').trim();
  return (
    info.length === 0 &&
    run[0] === input.open.marker &&
    run.length >= input.open.length
  );
}

function spanOf(input: {
  open: OpenFence;
  markdown: string;
  end: number;
  closed: boolean;
}): FenceSpan {
  const { open } = input;
  return {
    info: open.info,
    start: open.start,
    end: input.end,
    bodyStart: open.bodyStart,
    bodyEnd: open.bodyEnd,
    body: input.markdown.slice(open.bodyStart, open.bodyEnd),
    closed: input.closed,
  };
}

/** Every top-level fenced block in `markdown`, in document order, located.
 *
 *  A stateful scan rather than a `map`/`filter` chain: each line is read
 *  against the fence that is currently open, which is exactly the bookkeeping
 *  a functional iterator would have to carry by hand. `extract-map-fences.ts`
 *  reads the same way. */
export function scanFenceSpans(markdown: string): FenceSpan[] {
  const spans: FenceSpan[] = [];
  let open: OpenFence | null = null;

  for (const line of locateLines(markdown)) {
    if (open === null) {
      open = readOpener({ line });
      continue;
    }
    if (closes({ open, line })) {
      spans.push(spanOf({ open, markdown, end: line.end, closed: true }));
      open = null;
      continue;
    }
    open.bodyEnd = line.end;
  }

  if (open !== null) {
    open.bodyEnd = markdown.length;
    spans.push(spanOf({ open, markdown, end: markdown.length, closed: false }));
  }

  return spans;
}

/** The located blocks whose info string is exactly `info`. */
export function fenceSpansOfKind(input: {
  markdown: string;
  info: string;
}): FenceSpan[] {
  return scanFenceSpans(input.markdown).filter((span) => span.info === input.info);
}
